import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

export default function (param = {}) {
  const options = typeof param === 'object' ? param : {};
  const mapInputProps = typeof param === 'function' ? param : options.mapInputProps;

  const mapCompleteInputProps = props => {
    // In case mapInputProps is null
    if (typeof mapInputProps !== 'function') {
      return props;
    }

    return {
      ...props,
      ...mapInputProps(props),
    };
  };

  const { mapOutputProps = e => e, itemsChunkSize = 10 } = options;

  return function (Component) {
    return class extends PureComponent {
      static propTypes = {
        data: PropTypes.objectOf(
          PropTypes.shape({
            id: PropTypes.string,
          })
        ),
      };

      dataChunkSize = itemsChunkSize;

      state = {
        lastQuery: {},
        pagination: {
          total: 0,
          start: 0,
          size: 0,
          ids: [],
        },
        fetchingError: null,
      };

      setFetchingError = error =>
        new Promise(fulfill => {
          this.setState(
            {
              fetchingError: error,
            },
            fulfill
          );
        });

      flushFetchingError = () =>
        new Promise(fulfill => {
          this.setState(
            {
              fetchingError: null,
            },
            fulfill
          );
        });

      replenishItems = async removedItemIds => {
        const { find } = mapCompleteInputProps(this.props);
        const { lastQuery, pagination } = this.state;
        const { start, ids } = pagination;

        let paginationChunk;
        try {
          paginationChunk = await find({
            ...lastQuery,
            skip: start + ids.length - removedItemIds.length,
            take: removedItemIds.length,
          });
        } catch (error) {
          await this.setFetchingError(error);
          return;
        }

        this.setState({
          pagination: {
            ...pagination,
            total: paginationChunk.total,
            ids: _.chain(ids)
              .without(...removedItemIds)
              .concat(paginationChunk.ids)
              .uniq()
              .value(),
          },
        });
      };

      findNextItems = async (query = {}, options = {}) => {
        const { find } = mapCompleteInputProps(this.props);

        const { flush = false, limit = this.dataChunkSize } = options;

        let { pagination } = this.state;
        if (flush === true) {
          pagination = {
            total: 0,
            start: 0,
            size: 0,
            ids: [],
          };
        }

        const { start, size, ids } = pagination;

        const finalQuery = _.isPlainObject(query) ? query : {};
        let paginationChunk;
        try {
          paginationChunk = await find({
            ...finalQuery,
            skip: start + size,
            take: limit,
          });

          await this.flushFetchingError();
        } catch (error) {
          await this.setFetchingError(error);
          return;
        }

        if (paginationChunk instanceof Error) {
          return;
        }

        return new Promise(fulfill =>
          this.setState(
            {
              lastQuery: finalQuery,
              pagination: {
                ...pagination,
                total: paginationChunk.total,
                start,
                size: size + limit,
                ids: ids.concat(paginationChunk.ids),
              },
            },
            fulfill
          )
        );
      };

      removeItems = async (ids = []) => {
        const { remove } = mapCompleteInputProps(this.props);

        if (ids.length === 0) {
          return;
        }

        await remove(ids);
        await this.replenishItems(ids);
      };

      buildItems = () => {
        const { data } = mapCompleteInputProps(this.props);
        const { pagination } = this.state;
        const { ids } = pagination;

        return _.chain(ids)
          .map(id => data[id])
          .filter(Boolean)
          .value();
      };

      buildProps = () => {
        const { pagination } = this.state;

        const inputProps = mapCompleteInputProps(this.props);

        const outputProps = {
          items: this.buildItems(),
          pagination,
        };

        if (typeof inputProps.find === 'function') {
          outputProps.findNextItems = this.findNextItems;
        }

        if (typeof inputProps.remove === 'function') {
          outputProps.removeItems = this.removeItems;
        }

        // In case mapOutputProps is null
        if (typeof mapOutputProps !== 'function') {
          return outputProps;
        }

        return mapOutputProps(outputProps);
      };

      render() {
        const iteratorProps = this.buildProps();

        return <Component {...this.props} {...iteratorProps} />;
      }
    };
  };
}
